<template>
  <div class="for-transition w-100 flex flex-column gap-medium">
    <!-- Phone and Email -->
    <div>
      <h4 class="mn-b-small">{{ methods.phone ? t('auth.profileEdit.account.contactsTitle') : t('auth.profileEdit.account.emailTitle') }}</h4>
      <div class="card">
        <!-- Phone: только если вход по телефону включён (`options.auth.phone`). -->
        <div v-if="methods.phone" class="mn-b-medium">
          <Field
            v-if="users.state.current.phone && !isEditingPhone"
            v-model:field="users.state.current.phone"
            :label="t('auth.profileEdit.account.phone')"
            class="mn-b-thin input input--l"
            disabled
          />
          <FieldPhone
            v-else
            @change="(val) => newPhone = val"
            :dropdownOptions="{
              showDialCodeInSelection: true,
              showFlags: true,
              showDialCodeInList: true
            }"
            :defaultCountry="auth.state.user.country || ''"
            :validCharactersOnly="true"
            mode="national"
            :inputOptions="{ placeholder: t('auth.profileEdit.account.phonePlaceholder'), type: 'tel' }"
            class="input input--l mn-b-thin"
          />
          <Button
            v-if="users.state.current.phone && !isEditingPhone"
            :submit="() => isEditingPhone = true"
            class="button--card button--m w-100"
          >
            {{ t('auth.profileEdit.account.changePhone') }}
          </Button>
          <Button
            v-else-if="!users.state.current.phone"
            :submit="() => savePhone()"
            class="button--accent button--m w-100"
          >
            {{ t('auth.profileEdit.account.savePhone') }}
          </Button>
          <div v-else class="flex gap-small">
            <Button
              :submit="() => savePhone()"
              class="button--accent button--m flex-child-1"
            >
              {{ t('auth.profileEdit.account.savePhone') }}
            </Button>
            <Button
              :submit="() => { isEditingPhone = false; newPhone = '' }"
              class="button--card button--m flex-child-1"
            >
              {{ t('auth.profileEdit.account.cancel') }}
            </Button>
          </div>
        </div>

        <!-- Email -->
        <div>
          <Field
            v-if="users.state.current.email && !isEditingEmail"
            v-model:field="users.state.current.email"
            :label="t('auth.profileEdit.account.email')"
            class="mn-b-thin input input--l"
            disabled
          />
          <Field
            v-else
            v-model:field="newEmail"
            :label="t('auth.profileEdit.account.email')"
            :placeholder="t('auth.profileEdit.account.emailPlaceholder')"
            class="mn-b-thin input input--l"
          />
          <Button
            v-if="users.state.current.email && !isEditingEmail"
            :submit="() => isEditingEmail = true"
            class="button--card button--m w-100"
          >
            {{ t('auth.profileEdit.account.changeEmail') }}
          </Button>
          <Button
            v-else-if="!users.state.current.email"
            :submit="() => saveEmail()"
            class="button--accent button--m w-100"
          >
            {{ t('auth.profileEdit.account.saveEmail') }}
          </Button>
          <div v-else class="flex gap-small">
            <Button
              :submit="() => saveEmail()"
              class="button--accent button--m flex-child-1"
            >
              {{ t('auth.profileEdit.account.saveEmail') }}
            </Button>
            <Button
              :submit="() => { isEditingEmail = false; newEmail = '' }"
              class="button--card button--m flex-child-1"
            >
              {{ t('auth.profileEdit.account.cancel') }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- Language -->
    <div>
      <h4 class="mn-b-small">{{ t('auth.profileEdit.account.languageTitle') }}</h4>
      <div class="card">
        <Select
          :select="users.state.current.locale"
          :options="locale_options"
          property="id"
          :label="t('auth.profileEdit.account.language')"
          @update:select="saveLocale"
          class="w-100 input input--l"
        />
      </div>
    </div>

    <!-- Country -->
    <div>
      <h4 class="mn-b-small">{{ t('auth.profileEdit.account.countryTitle') }}</h4>
      <div class="card">
        <Select
          :select="users.state.current.country"
          :options="geoState.countries"
          property="id"
          :label="t('auth.profileEdit.account.country')"
          @update:select="saveCountry"
          class="w-100 input input--l"
        />
        <p class="footnote t-muted mn-t-small">{{ t('auth.profileEdit.account.countryHint') }}</p>
      </div>
    </div>

    <!-- Settings -->
    <div>
      <h4 class="mn-b-small">{{ t('auth.profileEdit.account.settingsTitle') }}</h4>
      <!-- Строка-переключатель — card, как строки каналов в «Уведомлениях»:
           текст слева (w-100 распирает строку), свитч справа. Тема применяется
           сразу — отдельного «Сохранить» на этой странице нет. -->
      <label class="card card--compact flex flex-v-center flex-between cursor-pointer">
        <span class="w-100">{{ t('auth.profileEdit.account.darkmode') }}</span>
        <Checkbox
          mode="switch"
          :radio="ds.theme.mode === 'dark'"
          @update:radio="() => ds.theme.toggle()"
          class="flex-shrink-0"
        />
      </label>
    </div>

    <!-- Account Actions -->
    <div>
      <h4 class="mn-b-small">{{ t('auth.profileEdit.account.accountTitle') }}</h4>
      <div class="flex flex-column gap-small">
        <Button
          :submit="logout"
          class="button--card button--l w-100"
        >
          {{ t('auth.profileEdit.account.logout') }}
        </Button>

        <Button
          :submit="onSubmitDelete"
          :callback="() => router.push({ name: 'Sign In' })"
          class="button--negative button--l w-100"
        >
          {{ t('auth.profileEdit.account.deleteAccount') }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import Checkbox from '@martyrs/src/design-system/components/Checkbox/Checkbox.vue'
import Button from '@martyrs/src/design-system/components/Button/Button.vue'
import { ds } from '@martyrs/src/design-system/index.js'
import Field from '@martyrs/src/design-system/components/Field/Field.vue'
import FieldPhone from '@martyrs/src/design-system/components/FieldPhone/FieldPhone.vue'
import Select from '@martyrs/src/design-system/components/Select/Select.vue'

import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { auth } from '@martyrs/src/modules/auth/views/store/auth.js'
import { users } from '@martyrs/src/modules/auth/views/store/users.js'
import { useStore, setError } from '@martyrs/src/modules/core/views/store/core.store.js'
import { withOtp } from '@martyrs/src/modules/auth/views/plugins/otp.plugin.js'
import { resolveAuthMethods } from '@martyrs/src/modules/auth/views/utils/auth-methods.js'
import $axios from '@martyrs/src/modules/core/views/utils/axios-instance.js'
import { i18nManager } from '@martyrs/src/modules/core/views/classes/i18n.manager.js'
import { actions as geoActions, state as geoState } from '@martyrs/src/modules/core/views/store/geo.store.js'

const store = useStore()
const core = store.core
const router = useRouter()
const { t } = useI18n()

// Способы входа проекта: блок телефона — только при входе по телефону.
const methods = resolveAuthMethods(core.state?.options?.auth)

const isEditingPhone = ref(false)
const isEditingEmail = ref(false)
const newPhone = ref('')
const newEmail = ref('')

defineProps({
  userData: {
    type: Object,
    required: true
  }
})

// Список языков берём из i18nManager: там же, где заданы поддерживаемые локали.
// localeNames — { ru: 'Русский', … }; если проект передал локали массивом,
// подписей нет и показываем сами коды. Select ждёт [{ id, name }].
const locale_options = computed(() => {
  if (i18nManager.localeNames) {
    return Object.entries(i18nManager.localeNames).map(([id, name]) => ({ id, name }))
  }
  return (i18nManager.supportedLocales || []).map(id => ({ id, name: id }))
})

onMounted(() => {
  geoActions.readCountries()
})

async function saveLocale(locale) {
  if (!locale || locale === users.state.current.locale) return

  try {
    const response = await $axios.put(`/api/users/${users.state.current._id}/locale`, { locale })
    users.state.current.locale = response.data.locale
    auth.state.user.locale = response.data.locale
    // Кука — источник языка при восстановлении сессии, обновляем вместе со стором
    auth.setCookie('user', { ...(await auth.getCookie('user')), locale: response.data.locale })
  } catch (error) {
    setError(error)
  }
}

async function saveCountry(country) {
  if (!country || country === users.state.current.country) return

  try {
    const response = await $axios.put(`/api/users/${users.state.current._id}/country`, { country })
    users.state.current.country = response.data.country
    auth.state.user.country = response.data.country
    auth.setCookie('user', { ...(await auth.getCookie('user')), country: response.data.country })
  } catch (error) {
    setError(error)
  }
}

async function savePhone() {
  if (!newPhone.value) return

  try {
    const result = await withOtp(
      (data) => $axios.put(`/api/users/${users.state.current._id}/phone`, data),
      { phone: newPhone.value }
    )

    if (result.data) {
      users.state.current.phone = result.data.phone
    }

    isEditingPhone.value = false
    newPhone.value = ''
  } catch (error) {
    console.log('savePhone catch:', error)
    setError(error)
  }
}

async function saveEmail() {
  if (!newEmail.value) return

  try {
    const result = await withOtp(
      (data) => $axios.put(`/api/users/${users.state.current._id}/email`, data),
      { email: newEmail.value }
    )

    if (result.data) {
      users.state.current.email = result.data.email
    }

    isEditingEmail.value = false
    newEmail.value = ''
  } catch (error) {
    setError(error)
  }
}

async function onSubmitDelete() {
  const confirmation = window.confirm(t('auth.profileEdit.account.deleteConfirm'))

  if (!confirmation) return

  users.state.current.status = 'removed'
  await users.actions.update(users.state.current)
  auth.actions.logout()
}

function logout() {
  auth.actions.logout()
  router.push({ name: 'Sign In' })
}
</script>
